<template>
  <div class="ai-ds-param-designer">
    <div
      class="ai-ds-param-trigger"
      :class="{ filled: hasForm }"
      @click="open"
    >
      <Icon type="md-list-box" />
      <span>{{ hasForm ? '编辑参数表单' : '设计参数表单' }}</span>
      <span v-if="fieldCount" class="ai-ds-param-count">{{ fieldCount }} 字段</span>
    </div>
    <Button
      v-if="hasForm"
      type="text"
      size="small"
      icon="md-trash"
      class="ai-ds-param-clear"
      @click.stop="clear"
    />

    <Modal
      class-name="form-designer-modal ai-ds-form-designer-modal"
      v-model="visible"
      width="1400"
      title="参数表单设计器"
      footer-hide
      :styles="{ top: '24px' }"
    >
      <template v-if="visible">
        <div class="form-designer-shell">
          <BaseWinMenu
            class="form-designer-menu"
            :items="menus"
            :active="formView"
            @event="handleEvent"
          />
          <div class="form-designer-body">
            <keep-alive>
              <template v-if="formView === 'editor'">
                <FormEditor ref="editor" v-model="form" class="form-designer-editor" />
              </template>
              <template v-else-if="formView === 'test'">
                <FormTest :form="form" />
              </template>
              <template v-else-if="formView === 'json'">
                <div class="form-json-wrap">
                  <div class="flex-wrap flex-between" style="margin-bottom:8px;">
                    <span style="font-size:12px;color:#888;">当前表单 JSON（保存时写入 param_form）</span>
                    <Button size="small" @click="copyFormJson">复制</Button>
                  </div>
                  <pre class="form-json-pre">{{ formJsonText }}</pre>
                </div>
              </template>
            </keep-alive>
          </div>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script>
import FormEditor from '@/components/form/editor';
import FormTest from '@/components/form/test';
import {cloneDeep} from 'lodash';

function emptyForm() {
  return {
    id: 'param_form',
    name: '参数表单',
    config: {},
    fields: [],
  };
}

function normalizeForm(v) {
  if (!v || typeof v !== 'object') return emptyForm();
  const form = cloneDeep(v);
  if (!Array.isArray(form.fields)) form.fields = [];
  if (!form.config || typeof form.config !== 'object') form.config = {};
  if (!form.name) form.name = '参数表单';
  if (!form.id) form.id = 'param_form';
  return form;
}

export default {
  name: 'AiDataSourceParamFormDesigner',
  components: {FormEditor, FormTest},
  props: {
    value: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      visible: false,
      formView: 'editor',
      form: emptyForm(),
    };
  },
  computed: {
    hasForm() {
      return !!(this.value && Array.isArray(this.value.fields) && this.value.fields.length);
    },
    fieldCount() {
      return this.hasForm ? this.value.fields.length : 0;
    },
    menus() {
      return [
        {icon: 'edit', name: '表单设计', key: 'editor'},
        {icon: 'richangbaoji01', name: '表单测试', key: 'test'},
        {icon: 'md-code-working', name: 'JSON', key: 'json'},
        {icon: 'save', name: '保存结构', right: true, key: 'save'},
      ];
    },
    formJsonText() {
      try {
        return JSON.stringify(this.form || {}, null, 2);
      } catch (e) {
        return String(e);
      }
    },
  },
  methods: {
    open() {
      this.form = normalizeForm(this.value);
      this.formView = 'editor';
      this.visible = true;
    },
    clear() {
      this.$emit('input', null);
    },
    handleSave() {
      if (this.$refs.editor && typeof this.$refs.editor.save === 'function') {
        this.$refs.editor.save();
      }
      const form = normalizeForm(this.form);
      (form.fields || []).forEach((v, i) => {
        v.order = i + 1;
      });
      // 只保存 JSON 结构，不写入 forms 表
      this.$emit('input', form);
      this.visible = false;
      if (this.$Message) this.$Message.success('参数表单结构已保存');
    },
    handleEvent(e) {
      if (e === 'save') {
        this.handleSave();
      } else {
        this.formView = e;
      }
    },
    copyFormJson() {
      if (!this.formJsonText || !navigator.clipboard) return;
      navigator.clipboard.writeText(this.formJsonText).then(() => {
        if (this.$Message) this.$Message.success('已复制');
      });
    },
  },
};
</script>

<style lang="less">
.ai-ds-param-designer {
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
}
.ai-ds-param-trigger {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 6px;
  min-height: 32px;
  padding: 0 10px;
  border: 1px solid var(--border, #dcdee2);
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  color: #aaa;
  background: #fff;
  &:hover {
    border-color: var(--primary, #2d8cf0);
  }
  &.filled {
    color: var(--primary, #2d8cf0);
  }
}
.ai-ds-param-count {
  margin-left: auto;
  font-size: 12px;
  color: #888;
}
.ai-ds-param-clear {
  flex-shrink: 0;
}

.ai-ds-form-designer-modal {
  --bg1: #f5f6f8 !important;
  --bg2: #ffffff !important;
  --bg3: #ffffff !important;
  --text1: #1f2329 !important;
  --text2: #333333 !important;
  --subtext2: #86909c !important;
  --border: #e5e6eb !important;
  --primary: #2d8cf0 !important;
  color: #333;

  .ivu-modal {
    top: 24px !important;
  }
  .ivu-modal-content {
    background: #fff !important;
    color: #333 !important;
  }
  .ivu-modal-header {
    background: #fff !important;
    border-bottom: 1px solid #e8e8e8 !important;
  }
  .ivu-modal-body {
    padding: 0 !important;
    height: calc(100vh - 96px);
    max-height: calc(100vh - 96px);
    overflow: hidden;
    background: #fff !important;
  }
  .form-designer-shell {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
  }
  .form-designer-menu {
    flex-shrink: 0;
    background: #eee;
    border-bottom: 1px solid var(--border);
    color: #333;
  }
  .form-designer-body {
    flex: 1;
    min-height: 0;
    overflow: hidden;
  }
  .form-designer-editor {
    height: 100%;
  }
  .form-json-wrap {
    height: 100%;
    padding: 12px 16px;
    overflow: auto;
    background: #fafbfc;
  }
  .form-json-pre {
    margin: 0;
    padding: 12px;
    background: #1e1e1e;
    color: #d4d4d4;
    border-radius: 4px;
    font-size: 12px;
    line-height: 1.5;
    white-space: pre-wrap;
    word-break: break-all;
  }
}
</style>
